import { BellAlert } from "@medusajs/icons"
import { Button, Drawer, Prompt, Text, clx, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { errorMessage } from "@/lib/sdk"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { alertTarget, enablePush, pushSupport, syncPush, useAlertSettings, useAlerts, useMarkRead, type AlertTab, type StaffAlert } from "@/lib/staff-alerts"
import { Tabs } from "@/terabizz"

/**
 * The bell (staff notifications) and the "Turn on notifications on this device" prompt.
 * Mounted once in the app shell.
 */

const ago = (s: string) => {
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}
const DOT: Record<string, string> = { critical: "bg-[#c0262d]", high: "bg-[#b7791f]", medium: "bg-[color:var(--k-bar)]" }

const TABS: { id: AlertTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "customers", label: "Customers & messages" },
  { id: "orders", label: "Orders" },
  { id: "security", label: "Security & system" },
]

export function AlertsBell({ className, onOpen }: { className?: string; onOpen?: () => void }) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<AlertTab>("all")
  const [expanded, setExpanded] = useState<string | null>(null)
  const { data } = useAlerts(true, tab)
  const markRead = useMarkRead()
  const navigate = useNavigate()
  const can = useCan()
  const unread = data?.unread ?? 0
  const go = (a: StaffAlert) => {
    if (!a.read_at) markRead.mutate({ ids: [a.id] })
    const target = alertTarget(a, can)
    if (target) {
      try {
        const u = new URL(target, window.location.origin)
        if (u.origin === window.location.origin) {
          setOpen(false)
          navigate(u.pathname + u.search)
          return
        }
      } catch {}
    }
    // Nothing to open: show the whole notification here instead of closing on nothing.
    setExpanded((x) => (x === a.id ? null : a.id))
  }
  return (
    <>
      <button
        type="button"
        onClick={() => (setOpen(true), onOpen?.())}
        className={clx("relative rounded-md p-1 hover:bg-[var(--tf-sidebar-hover)]", className)}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
      >
        <BellAlert />
        {unread ? (
          <span className={clx("absolute -right-1 -top-1 min-w-[16px] rounded-full px-1 text-center text-[10px] font-semibold leading-4 text-white", data?.unread_critical ? "bg-[var(--k-alert-fill)]" : "bg-[var(--k-blue-fill)]")}>
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
      <Drawer open={open} onOpenChange={setOpen}>
        <Drawer.Content>
          <Drawer.Header>
            <Drawer.Title>Notifications</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body className="overflow-y-auto p-0">
            <div className="border-b border-ui-border-base">
              <Tabs label="Notification type" value={tab} onChange={(v) => (setTab(v as AlertTab), setExpanded(null))} tabs={TABS.map((t) => ({ id: t.id, label: t.label, count: data?.unread_by_tab?.[t.id] || null }))} />
            </div>
            <div className="flex items-center justify-between border-b border-ui-border-base px-4 py-2">
              <Text size="small" className="text-ui-fg-subtle">{unread ? `${unread} unread` : "All caught up"}</Text>
              <div className="flex gap-x-3">
                {unread ? (
                  <button type="button" className="txt-compact-small text-ui-fg-interactive hover:underline" onClick={() => markRead.mutate({ all: true })}>
                    Mark all read
                  </button>
                ) : null}
                <button type="button" className="txt-compact-small text-ui-fg-interactive hover:underline" onClick={() => (setOpen(false), navigate("/settings/notifications/staff"))}>
                  Settings
                </button>
              </div>
            </div>
            {!data?.alerts.length ? (
              <p className="px-4 py-10 text-center text-[13px] text-ui-fg-subtle">
                {tab === "all" ? "Nothing yet. New orders, customer messages, security changes, stock alerts and problems show up here." : "Nothing here yet."}
              </p>
            ) : (
              <ul>
                {data.alerts.map((a) => {
                  const isOpen = expanded === a.id
                  const linked = !!alertTarget(a, can)
                  return (
                    <li key={a.id} className="border-b border-ui-border-base">
                      <button
                        type="button"
                        onClick={() => go(a)}
                        aria-expanded={linked ? undefined : isOpen}
                        className={clx("flex w-full gap-x-3 px-4 py-3 text-left hover:bg-ui-bg-base-hover", !a.read_at && "bg-[color:var(--k-info-bg-faint)]")}
                      >
                        <span className={clx("mt-1.5 size-2 shrink-0 rounded-full", a.read_at ? "bg-transparent" : DOT[a.priority])} />
                        <span className="min-w-0 flex-1">
                          <span className={clx("block text-[13px]", !a.read_at && "font-semibold", !isOpen && "line-clamp-2")}>{a.title}</span>
                          {a.body ? <span className={clx("block break-words text-[12px] text-ui-fg-subtle", isOpen ? "whitespace-pre-wrap" : "line-clamp-2")}>{a.body}</span> : null}
                          <span className="block text-[11px] text-ui-fg-muted">
                            {a.priority === "critical" ? "Security / critical · " : ""}
                            {a.label}
                            {a.actor_label ? ` · by ${a.actor_label}` : ""} · {ago(a.created_at)}
                          </span>
                          {isOpen ? (
                            <span className="mt-1.5 block text-[11px] text-ui-fg-muted">
                              {new Date(a.created_at).toLocaleString()} · No page to open for this notification.
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer>
    </>
  )
}

const promptKey = (userId: string) => `terify-push-prompt:${userId}`
const ASK_AGAIN_MS = 3 * 24 * 3600_000

/**
 * After sign-in, on every device: if this person gets staff notifications and this browser
 * hasn't been turned on (or turned down) yet, a screen asks to turn on notifications here.
 * "Not now" asks again in 3 days. Browsers that already allowed it are re-registered silently.
 */
export function EnablePushPrompt() {
  const me = useCurrentMe()
  const { data: settings } = useAlertSettings()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const support = typeof window !== "undefined" ? pushSupport() : "unsupported"

  useEffect(() => {
    if (!settings?.prefs.enabled) return
    if (support === "ok" && Notification.permission === "granted") {
      void syncPush(settings.public_key)
      return
    }
    if (support === "unsupported" || support === "insecure") return
    if (support === "ok" && Notification.permission === "denied") return
    let last = 0
    try {
      last = Number(localStorage.getItem(promptKey(me.user.id)) ?? 0)
    } catch {}
    if (Date.now() - last > ASK_AGAIN_MS) setOpen(true)
  }, [settings?.prefs.enabled, settings?.public_key, support, me.user.id])

  const later = () => {
    try {
      localStorage.setItem(promptKey(me.user.id), String(Date.now()))
    } catch {}
    setOpen(false)
  }
  const turnOn = async () => {
    if (!settings) return
    setBusy(true)
    try {
      const r = await enablePush(settings.public_key)
      if (r === "on") {
        toast.success("Notifications are on for this device")
        setOpen(false)
      } else if (r === "denied") {
        toast.error("Notifications were blocked. Allow them for this site in your browser settings, then turn them on in Settings → Notifications.")
        later()
      } else later()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null
  return (
    <Prompt open={open} onOpenChange={(o) => (o ? setOpen(true) : later())}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>Turn on notifications on this device</Prompt.Title>
          <Prompt.Description>
            Get an alert on this {/iPhone|Android|iPad/.test(navigator.userAgent) ? "phone" : "computer"} the moment something needs you: new orders, refunds, security changes (new sign-ins, password or permission changes), low stock, bad reviews and payment problems.
          </Prompt.Description>
        </Prompt.Header>
        <div className="px-6 pb-2 text-[13px] text-ui-fg-subtle">
          {support === "ios_needs_install" ? (
            <div className="rounded-lg bg-ui-bg-subtle p-3 text-ui-fg-base">
              <strong>On iPhone and iPad</strong>, notifications only work from the Home Screen app:
              <ol className="mt-1 list-decimal pl-5">
                <li>Tap the Share button in Safari.</li>
                <li>Choose <em>Add to Home Screen</em>, then Add.</li>
                <li>Open Keel from your Home Screen, sign in, and tap Turn on.</li>
              </ol>
            </div>
          ) : (
            "Your browser will ask to allow notifications. Choose which ones you get in Settings → Notifications; security alerts always come through."
          )}
        </div>
        <Prompt.Footer>
          <Prompt.Cancel onClick={later}>Not now</Prompt.Cancel>
          {support === "ok" ? (
            <Button size="small" onClick={turnOn} isLoading={busy}>Turn on</Button>
          ) : (
            <Prompt.Action onClick={later}>Got it</Prompt.Action>
          )}
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  )
}
